<template>
  <div class="columns">
    <b-field
      label="Title"
      name="title"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('title') }"
      :message="hasError('title')"
    >
      <b-autocomplete
        open-on-focus
        rounded
        v-model="student.personalInformation.title"
        :data="filteredTitles"
        icon="magnify"
        clearable
        @select="(option) => (student.personalInformation.title = option)"
      >
        <template #empty>No results found</template>
      </b-autocomplete>
    </b-field>

    <b-field
      label="Full name as in passport"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('first_name') }"
      :message="hasError('first_name')"
    >
      <b-input
        v-model="student.personalInformation.first_name"
        type="text"
        name="first_name"
      ></b-input>
    </b-field>

    <b-field
      label="Full surname as in passport"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('last_name') }"
      :message="hasError('last_name')"
    >
      <b-input
        v-model="student.personalInformation.last_name"
        type="text"
        name="last_name"
      ></b-input>
    </b-field>

    <b-field
      label="Email address"
      class="column is-one-third"
      :type="validEmail.type"
      :message="validEmail.message"
    >
      <b-input
        v-model="student.personalInformation.email"
        type="text"
        name="email"
      ></b-input>
    </b-field>

    <b-field
      label="Email address copy"
      class="column is-one-third"
      :type="validEmail.type"
      :message="validEmail.message"
    >
      <b-input
        v-model="student.personalInformation.email_copy"
        type="text"
        name="email_copy"
        onpaste="return false;"
        onCopy="return false"
        onCut="return false"
        onDrag="return false"
        onDrop="return false"
        autocomplete="off"
      ></b-input>
    </b-field>

    <b-field
      label="Phone number"
      class="column col-sm-12 col-md-6 col-lg-3"
      :type="validatePhoneNumber.type"
      :message="validatePhoneNumber.message"
    >
      <template #label>
        Phone number
        <b-tooltip type="is-dark" label="UK phone numbers only">
          <b-icon size="is-small" icon="help-circle-outline"></b-icon>
        </b-tooltip>
      </template>
      <b-input
        v-model="student.personalInformation.phone_number"
        type="text"
        name="phone_number"
      ></b-input>
    </b-field>

    <b-field
      label="Intake"
      class="column is-one-third field-with-button"
      name="intake"
      :type="{ 'is-danger': hasError('intake') }"
      :message="hasError('intake')"
    >
      <b-select
        placeholder="See applications that have this intake:"
        v-model="student.personalInformation.intake"
      >
        <option v-for="(value, i) in filteredIntake" :key="i" :value="value">
          {{ value }}
        </option>
      </b-select>

      <b-button @click="loadMore = !loadMore"
        >Load {{ loadMore ? 'less' : 'more' }}</b-button
      >
    </b-field>

    <b-field
      label="Nationality"
      name="nationality"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('nationality') }"
      :message="hasError('nationality')"
    >
      <b-autocomplete
        open-on-focus
        rounded
        v-model="student.personalInformation.nationality"
        :data="filteredCountries('nationality')"
        icon="magnify"
        clearable
        @select="(option) => (student.personalInformation.nationality = option)"
      >
        <template #empty>No results found</template>
      </b-autocomplete>
    </b-field>

    <b-field
      v-if="!isEuStudent"
      :required="!isEuStudent"
      label="Issue date of residency permit"
      name="issueDateResidencyPermit"
      class="column is-one-third"
      :type="{ 'is-danger': hasError('issueDateResidencyPermit') }"
      :message="hasError('issueDateResidencyPermit')"
    >
      <b-datepicker
        placeholder="Click to select..."
        v-model="student.personalInformation.issueDateResidencyPermit"
      >
      </b-datepicker>
    </b-field>
  </div>
</template>

<script>
import { userTitles } from '../../models/static';
import { countryList, EUCountryList } from '../../models/static';
export default {
  name: 'initial-information',
  props: ['studentPersonalInformation', 'formErrors'],
  data() {
    return {
      student: {
        personalInformation: {
          title: '',
          first_name: '',
          last_name: '',
          intake: '',
          nationality: '',
          phone_number: '+44',
          email: '',
          email_copy: '',
          issueDateResidencyPermit: null,
        },
      },
      loadMore: false,
      intakes: [],
    };
  },
  created() {
    let studentPersonalInformation = this.studentPersonalInformation;
    this.intakes = this.activeIntakes;
    this.student.personalInformation = {
      ...studentPersonalInformation,
      issueDateResidencyPermit:
        studentPersonalInformation.issueDateResidencyPermit
          ? new Date(studentPersonalInformation.issueDateResidencyPermit)
          : null,
    };
  },
  computed: {
    filteredTitles() {
      return this.student.personalInformation.title
        ? userTitles.filter((option) => {
            return (
              option
                .toString()
                .toLowerCase()
                .indexOf(
                  this.student.personalInformation.title.toLowerCase()
                ) >= 0
            );
          })
        : userTitles;
    },
    isEuStudent() {
      return this.student.personalInformation.nationality
        ? EUCountryList.includes(this.student.personalInformation.nationality)
        : true;
    },
    validEmail() {
      const validEmail = this.validateEmail(
        this.student.personalInformation.email
      );
      const sameEmail =
        this.student.personalInformation.email ===
        this.student.personalInformation.email_copy;

      if (!validEmail) {
        return {
          isValid: false,
          type: 'is-danger',
          message: 'This email is invalid',
        };
      } else if (!sameEmail) {
        return {
          isValid: false,
          type: 'is-danger',
          message: 'Emails must be the same',
        };
      } else {
        return {
          isValid: true,
          type: 'is-success',
          message: '',
        };
      }
    },
    validatePhoneNumber() {
      const phoneNo = this.student.personalInformation.phone_number;
      if (phoneNo && phoneNo.toString().replace(/\D/g, '').length >= 10) {
        return {
          isValid: true,
          type: 'is-success',
          message: '',
        };
      } else {
        return {
          isValid: false,
          type: 'is-danger',
          message: 'Phone number is not a valid UK number',
        };
      }
    },
    activeIntakes() {
      return this.$store.state.application_filter.filterOptions.intake
        .filter((intake) => intake.value !== 'None')
        .map((intake) => intake.value)
        .slice(0, this.loadMore ? undefined : 1);
    },
    filteredIntake() {
      const studentIntake = this.student.personalInformation.intake;
      if (
        studentIntake &&
        studentIntake !== '' &&
        !this.activeIntakes.includes(studentIntake)
      ) {
        return this.activeIntakes.concat(studentIntake);
      }

      return this.activeIntakes;
    },
  },
  watch: {
    student: {
      deep: true,
      handler: function (value) {
        this.$emit('changed', value);
      },
    },
  },
  methods: {
    hasError(fieldName) {
      const fieldError = this.formErrors.filter(
        (error) => error.fieldName === fieldName
      );
      return fieldError && fieldError.length ? fieldError[0].message : null;
    },

    validateEmail(email) {
      const emailRegex =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return emailRegex.test(email);
    },

    filteredCountries(field) {
      if (!field || !this.student.personalInformation[field]) {
        return countryList;
      }

      return countryList.filter((option) => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.student.personalInformation[field].toLowerCase()) >= 0
        );
      });
    },
  },
};
</script>

<style scoped>
.columns {
  flex-wrap: wrap;
}

.tabs li.is-active a,
.tabs li a:hover,
.tabs li.is-active a span {
  text-decoration: none;
}

.custom-autocomplete {
  height: fit-content;
}

.action-buttons {
  justify-content: center;
  display: flex;
}

.action-buttons button {
  margin: 0 10px;
}

.parent-holder {
  margin-bottom: 40px;
}

.card-holder {
  margin-top: 30px;
  margin-left: 0px;
}

.hide-label > label {
  visibility: hidden;
}

.hide-label:nth-child(4n) > label,
.hide-label:nth-child(4n + 1) > label {
  display: none;
}

.field-with-button {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
